import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, query, orderBy, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ClipboardList, Handshake, CheckCircle2, Ticket, TrendingUp } from 'lucide-react';

const UNIT_PRICE = 10000;   // 테스트보철 건당 이용료

/** 청구서 인쇄 창 열기 */
function openInvoicePrint(inv, platform) {
    const w = window.open('', '_blank');
    if (!w) { alert('팝업이 차단되었습니다. 팝업을 허용해주세요.'); return; }
    const fmtW = n => (n || 0).toLocaleString('ko-KR');
    const rows = (inv.cases || []).map((c, i) => `
        <tr><td>${i + 1}</td><td>${c.testNumber}</td><td>${c.clinicName || '-'}</td><td>${c.acceptedDate || '-'}</td>
        <td class="r">${fmtW(UNIT_PRICE)}</td></tr>`).join('');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${inv.invoiceNo} 청구서</title>
    <style>
      body{font-family:-apple-system,"Pretendard","Apple SD Gothic Neo","Malgun Gothic",sans-serif;color:#151b26;max-width:720px;margin:0 auto;padding:44px 28px;line-height:1.6}
      h1{font-size:24px;letter-spacing:6px;text-align:center;border-bottom:2px solid #151b26;padding-bottom:14px;margin-bottom:22px}
      .meta{display:flex;justify-content:space-between;font-size:13px;margin-bottom:18px}
      .box{border:1px solid #d8dce3;border-radius:8px;padding:12px 16px;font-size:13px;width:47%}
      .box b{display:block;font-size:11px;color:#5b6472;letter-spacing:1px;margin-bottom:6px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:20px}
      th{background:#eef1f8;color:#1d3568;padding:9px 10px;border:1px solid #d8dce3;font-size:12px}
      td{padding:9px 10px;border:1px solid #e6e8ec}
      td.r{text-align:right;font-variant-numeric:tabular-nums}
      .total td{background:#eef1f8;font-weight:800}
      .note{font-size:12px;color:#5b6472;margin-top:18px;line-height:1.8}
      .foot{margin-top:34px;text-align:center;font-size:14px;font-weight:700}
      @media print{ .noprint{display:none} }
      .noprint{position:fixed;top:14px;right:14px}
      .noprint button{padding:10px 18px;font-size:14px;font-weight:700;cursor:pointer;border:none;border-radius:8px;background:#274690;color:#fff}
    </style></head><body>
    <div class="noprint"><button onclick="window.print()">인쇄</button></div>
    <h1>청 구 서</h1>
    <div class="meta"><span>청구서 번호: <b>${inv.invoiceNo}</b></span><span>발행일: ${inv.issueDate}</span></div>
    <div style="display:flex;justify-content:space-between;gap:12px">
      <div class="box"><b>공급자</b>${platform?.businessName || '덴탈클러스터'}${platform?.businessNumber ? `<br>사업자번호: ${platform.businessNumber}` : ''}${platform?.representative ? `<br>대표: ${platform.representative}` : ''}<br>연락처: 010-2972-3733</div>
      <div class="box"><b>공급받는자</b>${inv.labName}</div>
    </div>
    <table>
      <thead><tr><th>No</th><th>테스트번호</th><th>치과</th><th>수락일</th><th>금액(원)</th></tr></thead>
      <tbody>${rows}
        <tr class="total"><td colspan="3">합계 (${inv.count}건 × ${fmtW(UNIT_PRICE)}원)</td><td colspan="2" class="r">${fmtW(inv.amount)}원</td></tr>
      </tbody>
    </table>
    <div class="note">
      · 상기 금액은 덴탈클러스터 테스트보철(A/S) 매칭 이용료(건당 ${fmtW(UNIT_PRICE)}원)입니다.<br>
      · 각 건은 귀 기공소가 게시판에서 수락 시 동의한 이용 조건에 따라 청구됩니다. (부가세 별도 · 세금계산서 별도 발행)<br>
      · 문의: 010-2972-3733
    </div>
    <div class="foot">덴탈클러스터</div>
    </body></html>`);
    w.document.close();
}

/**
 * 관리자: 테스트보철 현황 (/admin/test-cases)
 * - 어느 치과 ↔ 어느 기공소가 테스트보철을 진행 중인지 전체 목록
 * - 요약: 모집중/진행중/완료 케이스 수, 이번 달 매칭 수, 이용권 매출
 */
const STATUS_LABEL = {
    open: { text: '모집중', color: '#059669', bg: '#ECFDF5' },
    accepted: { text: '매칭완료', color: '#2563EB', bg: '#EFF6FF' },
    in_progress: { text: '제작중', color: '#D97706', bg: '#FFFBEB' },
    delivered: { text: '납품완료', color: '#7C3AED', bg: '#F5F3FF' },
    completed: { text: '완료', color: '#6B7280', bg: '#F3F4F6' },
    cancelled: { text: '취소', color: '#DC2626', bg: '#FEF2F2' },
};

const fmt = (n) => (n || 0).toLocaleString('ko-KR');
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' };

function AdminTestCases() {
    const [rows, setRows] = useState([]);
    const [passTotal, setPassTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [invoices, setInvoices] = useState([]);
    const [platform, setPlatform] = useState(null);
    const [issuing, setIssuing] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                // 발행된 청구서 + 플랫폼 사업자 정보
                try {
                    const invSnap = await getDocs(collection(db, 'leadBillingInvoices'));
                    setInvoices(invSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
                    const pf = await getDoc(doc(db, 'config', 'platform'));
                    if (pf.exists()) setPlatform(pf.data());
                } catch (_) { }
                // 전체 테스트 케이스 + 비공개 정보(치과/기공소, 관리자 read 허용)
                const snap = await getDocs(query(collection(db, 'testRequests'), orderBy('createdAt', 'desc')));
                const list = await Promise.all(snap.docs.map(async d => {
                    let priv = {};
                    try {
                        const p = await getDoc(doc(db, 'testRequests', d.id, 'private', 'info'));
                        if (p.exists()) priv = p.data();
                    } catch (_) { /* 권한 없음 */ }
                    return { id: d.id, ...d.data(), priv };
                }));
                setRows(list);

                // 이용권 매출 합계
                const paySnap = await getDocs(collection(db, 'testPassPayments'));
                setPassTotal(paySnap.docs.reduce((s, d) => s + (d.data().amount || 0), 0));
            } catch (e) {
                console.error('테스트보철 현황 조회 실패:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // 청구서 발행: 해당 기공소의 미청구 수락 건을 묶어 저장 + 인쇄 창
    const issueInvoice = async (labName, cases) => {
        if (!window.confirm(`${labName}에 청구서를 발행할까요?\n\n미청구 ${cases.length}건 × 10,000원 = ${(cases.length * UNIT_PRICE).toLocaleString()}원`)) return;
        setIssuing(true);
        try {
            const today = new Date();
            const ymd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
            const invoiceNo = `DCB-${ymd}-${String(invoices.length + 1).padStart(3, '0')}`;
            const inv = {
                invoiceNo,
                labName,
                labId: cases[0]?.priv?.acceptedLabId || '',
                caseIds: cases.map(c => c.id),
                cases: cases.map(c => ({
                    testNumber: c.testNumber,
                    clinicName: c.priv?.clinicName || '',
                    acceptedDate: c.acceptedAt?.toDate ? c.acceptedAt.toDate().toLocaleDateString('ko-KR') : '',
                })),
                count: cases.length,
                unitPrice: UNIT_PRICE,
                amount: cases.length * UNIT_PRICE,
                issueDate: today.toLocaleDateString('ko-KR'),
                status: 'issued',   // issued → paid
                createdAt: serverTimestamp(),
            };
            await addDoc(collection(db, 'leadBillingInvoices'), inv);
            openInvoicePrint(inv, platform);
            setRefreshKey(k => k + 1);
        } catch (e) {
            alert('발행 실패: ' + e.message);
        } finally {
            setIssuing(false);
        }
    };
    const markPaid = async (inv, paid) => {
        try {
            await updateDoc(doc(db, 'leadBillingInvoices', inv.id), { status: paid ? 'paid' : 'issued' });
            setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: paid ? 'paid' : 'issued' } : i));
        } catch (e) { alert('상태 변경 실패: ' + e.message); }
    };

    const formatDate = (ts) => {
        if (!ts?.toDate) return '-';
        const d = ts.toDate();
        return `${String(d.getFullYear()).slice(2)}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    const thisMonth = (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    })();
    const acceptedThisMonth = rows.filter(r => {
        if (!r.acceptedAt?.toDate) return false;
        const d = r.acceptedAt.toDate();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === thisMonth;
    }).length;

    const inProgress = rows.filter(r => ['accepted', 'in_progress', 'delivered'].includes(r.status)).length;
    const openCount = rows.filter(r => r.status === 'open').length;
    const completedCount = rows.filter(r => r.status === 'completed').length;

    const filtered = statusFilter === 'all' ? rows
        : statusFilter === 'in_progress_all' ? rows.filter(r => ['accepted', 'in_progress', 'delivered'].includes(r.status))
        : rows.filter(r => r.status === statusFilter);

    const statCard = (label, value, color, Icon) => (
        <div style={{ flex: 1, minWidth: 150, border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
            <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={15} /> {label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 6 }}>{value}</div>
        </div>
    );

    return (
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 16px' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                <ClipboardList size={24} color="#2563EB" /> 테스트보철 현황
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20 }}>
                치과 ↔ 기공소 테스트보철 매칭 전체 현황입니다. 매칭 완료 시 partnerships가 생성되어 수수료 부과 대상이 됩니다.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                {statCard('모집중', `${openCount}건`, '#059669', ClipboardList)}
                {statCard('진행중 (매칭~납품)', `${inProgress}건`, '#2563EB', Handshake)}
                {statCard('완료', `${completedCount}건`, '#6B7280', CheckCircle2)}
                {statCard('이번 달 매칭', `${acceptedThisMonth}건`, '#7C3AED', TrendingUp)}
                {statCard('이용권 매출 (누적)', `${fmt(passTotal)}원`, '#D97706', Ticket)}
            </div>

            {/* 💰 기공소별 건당 청구 집계 (증빙) + 청구서 발행 */}
            {(() => {
                const billed = rows.filter(r => r.priv?.acceptUsage === 'manual_billing' && r.priv?.labName);
                if (billed.length === 0 && invoices.length === 0) return null;
                const invoicedIds = new Set(invoices.flatMap(i => i.caseIds || []));
                const byLab = {};
                billed.forEach(r => {
                    const k = r.priv.labName;
                    if (!byLab[k]) byLab[k] = [];
                    byLab[k].push(r);
                });
                return (
                    <>
                    <div style={{ border: '1.5px solid #274690', borderRadius: 12, background: '#fff', marginBottom: 14, overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <b style={{ fontSize: 15 }}>기공소별 수락 집계 — 건당 10,000원</b>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>총 {billed.length}건 · 미청구 {billed.filter(r => !invoicedIds.has(r.id)).length}건</span>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                                <thead>
                                    <tr style={{ background: '#EEF1F8', textAlign: 'left' }}>
                                        {['기공소', '미청구', '미청구 금액', '수락 케이스 (증빙)', ''].map((h, i) => (
                                            <th key={i} style={{ padding: '9px 14px', fontWeight: 700, color: '#1D3568', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(byLab).sort((a, b) => b[1].length - a[1].length).map(([lab, cases]) => {
                                        const unbilled = cases.filter(c => !invoicedIds.has(c.id));
                                        return (
                                        <tr key={lab}>
                                            <td style={{ ...tdStyle, fontWeight: 700 }}>{lab}</td>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>{unbilled.length}건 <span style={{ color: '#9CA3AF', fontWeight: 400 }}>/ 총 {cases.length}</span></td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#274690' }}>{fmt(unbilled.length * UNIT_PRICE)}원</td>
                                            <td style={{ ...tdStyle, whiteSpace: 'normal', fontSize: 12.5, color: '#374151' }}>
                                                {cases.map(c => `${c.testNumber} (${c.priv?.clinicName || '-'} · ${formatDate(c.acceptedAt)}${invoicedIds.has(c.id) ? ' · 청구됨' : ''})`).join('  ·  ')}
                                            </td>
                                            <td style={{ ...tdStyle }}>
                                                {unbilled.length > 0 && (
                                                    <button onClick={() => issueInvoice(lab, unbilled)} disabled={issuing}
                                                        style={{ background: '#274690', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                        청구서 발행
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ); })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {invoices.length > 0 && (
                        <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', marginBottom: 20, overflow: 'hidden' }}>
                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}><b style={{ fontSize: 14 }}>발행된 청구서</b></div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                                            {['청구서 번호', '기공소', '건수', '금액', '발행일', '상태', ''].map((h, i) => (
                                                <th key={i} style={{ padding: '9px 14px', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map(inv => (
                                            <tr key={inv.id}>
                                                <td style={{ ...tdStyle, fontWeight: 600 }}>{inv.invoiceNo}</td>
                                                <td style={tdStyle}>{inv.labName}</td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>{inv.count}건</td>
                                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(inv.amount)}원</td>
                                                <td style={tdStyle}>{inv.issueDate}</td>
                                                <td style={tdStyle}>
                                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: inv.status === 'paid' ? '#157F3D' : '#B45309' }}>
                                                        <input type="checkbox" checked={inv.status === 'paid'} onChange={e => markPaid(inv, e.target.checked)} style={{ accentColor: '#157F3D' }} />
                                                        {inv.status === 'paid' ? '입금 완료' : '입금 대기'}
                                                    </label>
                                                </td>
                                                <td style={tdStyle}>
                                                    <button onClick={() => openInvoicePrint(inv, platform)}
                                                        style={{ background: '#fff', border: '1px solid #D3D7DE', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', color: '#274690' }}>
                                                        인쇄 · 보기
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    </>
                );
            })()}

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {[['all', '전체'], ['open', '모집중'], ['in_progress_all', '진행중'], ['completed', '완료'], ['cancelled', '취소']].map(([key, label]) => (
                    <button key={key} onClick={() => setStatusFilter(key)}
                        style={{
                            padding: '8px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                            border: statusFilter === key ? '1px solid #2563EB' : '1px solid #E5E7EB',
                            background: statusFilter === key ? '#EFF6FF' : '#fff',
                            color: statusFilter === key ? '#2563EB' : '#6B7280',
                        }}>
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>불러오는 중...</div>
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff' }}>
                        <thead>
                            <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                                {['테스트번호', '상태', '치과', '기공소', '케이스', '등록일', '수락일', '수락 유형'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #E5E7EB', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => {
                                const s = STATUS_LABEL[r.status] || STATUS_LABEL.open;
                                return (
                                    <tr key={r.id}>
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>{r.testNumber}</td>
                                        <td style={tdStyle}>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '3px 8px', borderRadius: 10 }}>{s.text}</span>
                                        </td>
                                        <td style={tdStyle}>{r.priv?.clinicName || '-'}</td>
                                        <td style={{ ...tdStyle, fontWeight: r.priv?.labName ? 600 : 400, color: r.priv?.labName ? '#2563EB' : '#9CA3AF' }}>
                                            {r.priv?.labName || '(미수락)'}
                                        </td>
                                        <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: 260 }}>{r.caseInfo?.prosthesisType || '-'}</td>
                                        <td style={tdStyle}>{formatDate(r.createdAt)}</td>
                                        <td style={tdStyle}>{formatDate(r.acceptedAt)}</td>
                                        <td style={tdStyle}>
                                            {r.priv?.acceptUsage === 'free' ? <span style={{ color: '#059669', fontWeight: 600 }}>무료</span>
                                                : r.priv?.acceptUsage === 'paid' ? <span style={{ color: '#D97706', fontWeight: 600 }}>이용권</span>
                                                    : r.priv?.acceptUsage === 'manual_billing' ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>건당 청구</span>
                                                        : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>해당하는 케이스가 없습니다.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AdminTestCases;
